import { Component, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { DrObjectType, DrObject } from '../../../models/dr-object';
import { Endpoint, HelpIndex, MessageType, UpdateStatusType } from '../../../models/enums';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { NameDescriptionComponent } from '../../name-description/name-description.component';
import { isUndefined } from '../../../models/utils';
import { ViewType } from '../../../models/view';

export enum EditMode {
    TAG,
    EDITTAG, // If these get reordered, the HTML template for EditBucket needs to be modified to properly set the FA icon class
    FOLDER,
    EDITFOLDER,
    NEWREVIEWFOLDER,
    NEWREVIEWSUBFOLDER,
    EDITREVIEWFOLDER
}

@Component({
    standalone: false,
    selector: 'res-edit-bucket-modal',
    templateUrl: './edit-bucket-modal.component.html',
    styleUrls: ['./edit-bucket-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class EditBucketModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(NameDescriptionComponent, { static: true }) nameDescriptionComponent: NameDescriptionComponent;
    @ViewChild('tagsTemplate', { static: true }) tagsTemplate: TemplateRef<any>;

    modalTitle: string;
    verifyRequiredValidator = true;
    primaryButtonLabel = 'OK';
    secondaryButtonLabel = 'Cancel';
    modalHelpIndex: HelpIndex;
    modalAutomationId: string;
    modalClass: string;
    isBusy = false;
    isBusyMessage = '';
    validateName = true;
    isDescriptionHidden = false;
    isColorListTemplateHidden = false;

    colorList: Array<string>;
    selectedColor: string;

    editMode: EditMode;

    // name desc inputs
    validateNameRequest: {
        endpoint: Endpoint,
        request: (name: string) => void
    };


    private endpoint: Endpoint;

    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        this.colorList = new Array<string>();
        this.colorList.push('#000000', '#999999', '#FF66FF', '#9900FF', '#0066FF', '#00CCCC', '#66CC00', '#4D9900', '#E9C900', '#FF9900', '#FF0000');
        this.selectedColor = this.colorList[0];
        this.editMode = this.modalData?.mode;
        this.isDescriptionHidden = this.modalData?.isDescriptionHidden ? true : false;
        this.isColorListTemplateHidden = this.modalData?.isColorListTemplateHidden ? true : false;
        const crtHandle = this.commonService.currentProject ? this.commonService.currentProject.handle : this.modalData.handle;
        switch (this.editMode) {
            case EditMode.TAG:
                this.modalTitle = 'New Tag';
                this.modalAutomationId = 'new-tag';
                this.modalClass = 'new-tag-modal';
                this.modalHelpIndex = HelpIndex.myedisc_add_edit_tag_dlg;
                this.isBusyMessage = 'Creating tag...';
                this.endpoint = Endpoint.projectManager_createTag;
                this.nameDescriptionComponent.createDrObject(DrObjectType.TAG);
                this.validateNameRequest = {
                    endpoint: Endpoint.viewManager_validateName,
                    request: (name) => {
                        return {
                            name: this.nameDescriptionComponent.name,
                            projectHandle: crtHandle,
                            objectType: this.nameDescriptionComponent.drObject.objectType,
                            systemScope: this.modalData?.systemScope
                        };
                    }
                };
                break;
            case EditMode.NEWREVIEWSUBFOLDER:
            case EditMode.NEWREVIEWFOLDER:
                this.modalTitle = 'New Folder';
                this.modalAutomationId = 'new-review-folder';
                this.modalClass = 'new-review-folder-modal';
                this.modalHelpIndex = HelpIndex.myedisc_add_edit_tag_dlg; // update help index
                this.isBusyMessage = 'Creating review folder...';
                this.endpoint = Endpoint.orgManager_createReviewSystemFolder;
                this.nameDescriptionComponent.createDrObject(DrObjectType.REEFREVIEWFOLDER);
                break;
            case EditMode.EDITREVIEWFOLDER:
                this.modalTitle = 'Edit Folder';
                this.modalAutomationId = 'edit-review-folder';
                this.modalClass = 'edit-review-folder-modal';
                this.modalHelpIndex = HelpIndex.myedisc_add_edit_tag_dlg; // update help index
                this.endpoint = Endpoint.projectManager_updateReviewProjectFolder;
                this.nameDescriptionComponent.createDrObject(DrObjectType.REEFREVIEWFOLDER);
                this.isBusyMessage = 'Editing review folder name...';
                break;
            case EditMode.EDITTAG:
                this.modalTitle = 'Edit Tag';
                this.modalAutomationId = 'edit-tag';
                this.modalClass = 'edit-tag-modal';
                this.modalHelpIndex = HelpIndex.myedisc_add_edit_tag_dlg;
                this.isBusyMessage = 'Editing tag...';
                this.endpoint = Endpoint.tagManager_updateTag;
                this.nameDescriptionComponent.name = this.modalData.entity.name;
                this.nameDescriptionComponent.description = this.modalData.entity.description;
                if (this.modalData.entity.viewType === ViewType.TAG) {
                    this.selectedColor = this.colorList.find(color => color === this.modalData.entity?.iconColor?.toUpperCase()) || this.colorList[0];
                } else {
                    this.selectedColor = this.colorList.find(color => color === this.modalData.entity.displayValue) || this.colorList[0];
                }

                this.validateName = true;
                this.nameDescriptionComponent.createDrObject(DrObjectType.TAG);
                this.validateNameRequest = {
                    endpoint: Endpoint.viewManager_validateName,
                    request: (name) => {
                        return {
                            name: this.nameDescriptionComponent.name,
                            projectHandle: crtHandle,
                            objectType: this.nameDescriptionComponent.drObject.objectType,
                            systemScope: this.modalData?.systemScope
                        };
                    }
                };
                break;
            case EditMode.FOLDER:
                this.modalTitle = 'New Folder';
                this.modalAutomationId = 'new-folder';
                this.modalClass = 'new-folder-modal';
                this.modalHelpIndex = HelpIndex.myedisc_add_dynamicfolder_dlg;
                this.isBusyMessage = 'Creating Folder...';
                this.endpoint = Endpoint.projectManager_createFolder;
                this.nameDescriptionComponent.createDrObject(DrObjectType.DYNAMIC_FOLDER);
                this.validateNameRequest = {
                    endpoint: Endpoint.viewManager_validateName,
                    request: (name) => {
                        return {
                            name: this.nameDescriptionComponent.name,
                            projectHandle: crtHandle,
                            objectType: this.nameDescriptionComponent.drObject.objectType,
                        };
                    }
                };
                break;
            case EditMode.EDITFOLDER:
                this.modalTitle = 'Edit Folder';
                this.modalAutomationId = 'edit-folder';
                this.modalClass = 'edit-folder-modal';
                this.modalHelpIndex = HelpIndex.myedisc_add_dynamicfolder_dlg;
                this.isBusyMessage = 'Editing Folder...';
                this.endpoint = Endpoint.dynamicFolderManager_updateDynamicFolder;
                this.nameDescriptionComponent.name = this.modalData.entity.name;
                this.nameDescriptionComponent.description = this.modalData.entity.description;
                this.nameDescriptionComponent.createDrObject(DrObjectType.DYNAMIC_FOLDER);
                this.selectedColor = this.colorList.find(color => color === this.modalData.entity.color) || this.colorList[0];
                this.validateNameRequest = {
                    endpoint: Endpoint.viewManager_validateName,
                    request: (name) => {
                        return {
                            name: this.nameDescriptionComponent.name,
                            projectHandle: crtHandle,
                            objectType: this.nameDescriptionComponent.drObject.objectType
                        };
                    }
                };
                break;
        }
    }

    isPrimaryButtonEnabled(): boolean {
        let enabled = false;
        if (!this.isBusy) {
            if (!DrObject.isEmpty(this.nameDescriptionComponent.name)) {
                if (this.nameDescriptionComponent.isNameDirty() && (this.nameDescriptionComponent.formValid)) {
                    enabled = true;
                }

                if ((enabled === false) && (this.nameDescriptionComponent.isDescriptionDirty()) && (this.nameDescriptionComponent.formValid)) {
                    enabled = true;
                }

                if (enabled === false) {
                    if (this.modalData && this.modalData.entity) {
                        if (this.modalData.entity.color && this.selectedColor && (this.selectedColor !== this.modalData.entity.color)) {
                            enabled = true;
                        } else if (this.modalData.entity.displayValue && this.selectedColor && (this.selectedColor !== this.modalData.entity.displayValue)) {
                            enabled = true;
                        } else if (this.modalData.entity?.iconColor?.toUpperCase() && this.selectedColor && (this.selectedColor !== this.modalData.entity?.iconColor?.toUpperCase())) {
                            enabled = true;
                        }
                    }
                }
            }
        }
        return enabled;
    }


    ok(): void {
        if (this.nameDescriptionComponent.isNameDirty()) {
            this.nameDescriptionComponent.validate().then(valid => {
                if (valid) {
                    this.saveChanges();
                }
            });
        } else {
            if (this.nameDescriptionComponent.isDescriptionDirty() ||
                (!isUndefined(this.modalData.entity.displayValue) && (this.selectedColor !== this.modalData.entity.displayValue))) {
                this.saveChanges();
            } else if ((this.selectedColor !== this.modalData.entity.displayValue)) {
                this.saveChanges();
            }
        }
    }

    saveChanges(): void {
        this.isBusy = true;
        let params;
        switch (this.editMode) {
            case EditMode.TAG: {
                params = {
                    name: this.nameDescriptionComponent.name,
                    description: this.nameDescriptionComponent.description,
                    displayValue: this.selectedColor,
                    projectHandle: this.modalData.projectHandle || this.commonService.currentProject.handle,
                    enabled: true,
                    systemScope: this.modalData?.systemScope
                };
                break;
            }
            case EditMode.EDITTAG: {
                const tagHandle = this.modalData.entity.attributes?.length > 0 ? this.modalData.entity.attributes.find(a => a.name === 'tagHandle') : null;
                params = {
                    handle: tagHandle ? tagHandle.value : this.modalData.entity.handle,
                    name: this.nameDescriptionComponent.name,
                    description: this.nameDescriptionComponent.description,
                    displayValue: this.selectedColor,
                    enabled: true,
                    systemScope: this.modalData?.systemScope
                };
                break;
            }
            case EditMode.FOLDER: {
                params = {
                    name: this.nameDescriptionComponent.name,
                    description: this.nameDescriptionComponent.description,
                    color: this.selectedColor,
                    projectHandle: this.commonService.currentProject.handle
                };
                break;
            }
            case EditMode.EDITFOLDER: {
                params = {
                    name: this.nameDescriptionComponent.name,
                    description: this.nameDescriptionComponent.description,
                    archived: this.modalData.entity.archived,
                    color: this.selectedColor,
                    handle: this.modalData.entity.handle
                };
                break;
            }
            case EditMode.NEWREVIEWFOLDER: {
                params = {
                    folderName: this.nameDescriptionComponent.name,
                    projectHandle: this.commonService.currentProject.handle,
                    reviewProjectId: this.modalData.reviewProjectId,
                    reviewSystem: this.commonService.currentProject.reviewSystem
                };
                break;
            }
            case EditMode.NEWREVIEWSUBFOLDER: {
                params = {
                    folderName: this.nameDescriptionComponent.name,
                    parentFolderId: this.modalData.parentFolderId,
                    reviewProjectId: this.modalData.reviewProjectId,
                    reviewSystem: this.commonService.currentProject.reviewSystem
                };
                break;
            }
            case EditMode.EDITREVIEWFOLDER: {
                params = {
                    reviewSystem: this.commonService.currentProject.reviewSystem,
                    reviewProjectId: this.modalData.reviewProjectId,
                    folderNameId: this.modalData.parentFolderId,
                    newFolderName: this.nameDescriptionComponent.name,
                    byName: false
                };
                break;
            }
        }
        this.restService.post(this.endpoint, params).then(response => {
            if (response) {
                this.isBusy = false;
                this.commonService.sendMessage(MessageType.entityChanged,
                    (this.editMode === EditMode.TAG || this.editMode === EditMode.EDITTAG) ? UpdateStatusType.TAG_VIEW
                        : UpdateStatusType.DYNAMIC_FOLDER_VIEW);
                this.closeModal(true, response);
            }
        }, () => {
            this.isBusy = false;
        });
    }
}
